import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Drawer, Typography } from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmActionDialog({
  open,
  title,
  description,
  confirmText,
  cancelText,
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation('provider');
  const confirmLabel = confirmText ?? t('bookings.confirmDialogDefaults.confirm');
  const cancelLabel = cancelText ?? t('bookings.confirmDialogDefaults.cancel');
  const [confirmPressed, setConfirmPressed] = useState(false);
  const [cancelPressed, setCancelPressed] = useState(false);

  // Hero visual config based on danger flag
  const accent = danger ? '#ff4d4f' : '#1f8b4c';
  const accentDark = danger ? '#d93b3e' : '#27ae60';
  const heroIcon = danger ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />;

  const confirmStyle: CSSProperties = {
    width: '100%',
    minHeight: 48,
    padding: '0 16px',
    border: `1px solid ${accent}26`,
    borderRadius: 12,
    background: loading
      ? `linear-gradient(135deg, ${accent}cc 0%, ${accentDark}cc 100%)`
      : `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    boxShadow: `0 4px 14px ${accent}3a, inset 0 1px 0 rgba(255,255,255,0.18)`,
    transform: confirmPressed && !loading ? 'scale(0.985)' : 'scale(1)',
    transition: 'transform 0.1s ease, box-shadow 0.15s ease, background 0.15s ease',
    WebkitTapHighlightColor: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    outline: 'none',
    letterSpacing: 0.15,
    opacity: loading ? 0.92 : 1,
  };

  const cancelStyle: CSSProperties = {
    width: '100%',
    minHeight: 46,
    padding: '0 16px',
    border: 'none',
    borderRadius: 12,
    background: cancelPressed
      ? 'var(--bl-surface-soft, #ebedf0)'
      : 'var(--bl-surface-soft, #f7f8fa)',
    color: 'var(--bl-text-muted, #6b7280)',
    fontSize: 14,
    fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer',
    transform: cancelPressed && !loading ? 'scale(0.985)' : 'scale(1)',
    transition: 'background 0.15s ease, transform 0.1s ease',
    WebkitTapHighlightColor: 'transparent',
    opacity: loading ? 0.55 : 1,
    outline: 'none',
  };

  return (
    <Drawer
      open={open}
      placement="bottom"
      onClose={onCancel}
      closable={false}
      maskClosable={!loading}
      height="auto"
      styles={{
        body: {
          padding: '12px 16px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          background: 'var(--bl-surface, #fff)',
        },
        wrapper: { borderRadius: '20px 20px 0 0', overflow: 'hidden' },
        mask: { background: 'rgba(15,23,42,0.45)' },
      }}
    >
      {/* Drag handle */}
      <div
        aria-hidden
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: 'var(--bl-divider, #e5e7eb)',
          margin: '0 auto 18px',
        }}
      />

      {/* Hero icon disc */}
      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${accent}14 0%, ${accent}28 100%)`,
            border: `1px solid ${accent}1f`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: accent,
            boxShadow: `0 6px 18px ${accent}1a`,
          }}
        >
          {heroIcon}
        </div>
      </div>

      {/* Title + description */}
      <div style={{ textAlign: 'center', marginBottom: 20, padding: '0 4px' }}>
        <Typography.Text
          style={{
            fontSize: 17,
            fontWeight: 700,
            display: 'block',
            color: 'var(--bl-text-strong, #0f172a)',
            letterSpacing: -0.2,
            marginBottom: description ? 6 : 0,
          }}
        >
          {title}
        </Typography.Text>
        {description && (
          <Typography.Text
            style={{
              fontSize: 13,
              color: 'var(--bl-text-muted, #6b7280)',
              display: 'block',
              maxWidth: 300,
              margin: '0 auto',
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography.Text>
        )}
      </div>

      {/* Confirm button */}
      <button
        type="button"
        onClick={() => !loading && onConfirm()}
        disabled={loading}
        onTouchStart={() => !loading && setConfirmPressed(true)}
        onTouchEnd={() => setConfirmPressed(false)}
        onTouchCancel={() => setConfirmPressed(false)}
        onMouseDown={() => !loading && setConfirmPressed(true)}
        onMouseUp={() => setConfirmPressed(false)}
        onMouseLeave={() => setConfirmPressed(false)}
        style={{ ...confirmStyle, marginBottom: 10 }}
      >
        {loading && <LoadingOutlined spin style={{ fontSize: 15 }} />}
        <span>{confirmLabel}</span>
      </button>

      {/* Cancel button */}
      <button
        type="button"
        onClick={() => !loading && onCancel()}
        disabled={loading}
        onTouchStart={() => !loading && setCancelPressed(true)}
        onTouchEnd={() => setCancelPressed(false)}
        onTouchCancel={() => setCancelPressed(false)}
        onMouseDown={() => !loading && setCancelPressed(true)}
        onMouseUp={() => setCancelPressed(false)}
        onMouseLeave={() => setCancelPressed(false)}
        style={cancelStyle}
      >
        {cancelLabel}
      </button>
    </Drawer>
  );
}
